<script setup lang="ts">
/**
 * The body of the full-screen result — personal figures, the payout ribbon and
 * the scoreboard — under the banner `GameHud` draws on the screen a decided
 * battle freezes on.
 *
 * ── Why this screen exists at all ────────────────────────────────────────────
 *
 * A decided battle **freezes rather than tearing the room down**, and `CLAUDE.md`
 * gives the reason: there is a result worth reading and disconnecting people at
 * the moment they want to read it is the worst possible time. This screen is what
 * makes the freeze worth it: who did what, what it pays, and where that leaves
 * the player's rank.
 *
 * It takes the whole viewport now, deliberately. The old version was one HUD
 * panel over the frozen arena, styled like `.roster` so it would not read as
 * having paused the game — but a decided arena has stopped broadcasting, the
 * input source is suspended and pointer lock is released, so there is nothing
 * behind it left to play. A debrief is its own screen, not a notice over one.
 *
 * Precisely: the arena emits **one** last snapshot, the one at the end of the
 * tick it was decided on, and then `Arena.step` returns before `onSnapshot` for
 * good. Everything on this screen therefore has to be either in that snapshot or
 * already held by the scene — there is no later read. That is why
 * `Arena.finishMission` had to start raising `battleOver` into `events` instead
 * of `pendingEvents`: a mission's copy of the event was being drained into a tick
 * that broadcasts nothing.
 *
 * ── The reward shown here is the reward banked, and that is engineered ───────
 *
 * This screen used to refuse to show any figure for the sortie, and it was right
 * to: credits and XP were paid off *time in the arena*, the player was still in
 * the arena reading the screen, so any number drawn would have been understated
 * by however long they looked at it. The rebalance dissolved that argument from
 * the server side rather than papering over it here — the payout clock stops at
 * the deciding tick (`Arena.secondsCredited`), kills, damage, the win and the
 * distance are all frozen with the arena, so every input to the payout is final
 * the moment this screen exists. `BattleTally.reward` is computed by the same
 * pure `rewardBreakdown` that `closeSession` banks through; the figures here
 * *are* the base figures the ledger records.
 *
 * The one honest gap left is boosts, which are read at banking because a boost
 * bought mid-sortie applies to that sortie — so the note under the rank bar says
 * base figures, boosts on top. There is deliberately no second statement of the
 * payout anywhere: the garage's old "Battle report" popup re-showed these same
 * figures on the way back and was read as an *extra* reward, so it was removed
 * — one battle, one payout, shown here and nowhere else.
 *
 * ── The ribbon is the signature, and it is the payout's identity ─────────────
 *
 * The full-bleed band crossing the screen is where the money is — itemised on
 * the left, totalled on the right, the rank bar under both. Its diagonal ember
 * striping is the game's own hazard treatment (the Foundry Hazard livery is
 * built on exactly this value structure), and it stays the band's identity in
 * victory and defeat alike: the *banner* carries the result, the ribbon carries
 * the payout, and colouring the payout by the result would conflate two
 * different facts. The tutorial gets the same band muted to steel, carrying the
 * pays-nothing statement in the place the payout would be — silence there would
 * read as a screen that forgot.
 *
 * ── The scoreboard restates nothing the wire did not already carry ───────────
 *
 * Names, kills, deaths and sides come off the roster the scene is still holding
 * from the last snapshot; damage comes off `battleOver`'s one-per-arena
 * `summary`, joined by `playerId`. Zero and unknown stay different facts: a
 * missing summary drops the damage column rather than reading 0 for a player
 * who had been shooting all match.
 *
 * ── It reads correctly for every mode that can reach it ──────────────────────
 *
 * Six can now: a team battle at `ROUNDS_TO_WIN`, the four missions through
 * `finishMission`, and a deathmatch at its timer or kill target.
 *
 * **The tutorial used to be the one that could not**, because its endless
 * deathmatch never set `outcome` — which was what stopped this screen promising a
 * learner a payout the drill yard does not make, by construction rather than by
 * a guard. It is a mission now, so it *does* reach here, and finishing seven
 * drills deserves a result screen.
 *
 * What it does not deserve is a reward block. The tutorial banks nothing: it
 * opens no session, so `closeSession` is never reached and every figure
 * `battleTallies` computed for it is one nobody will ever be credited. A screen
 * reading "+24 credits" over an account whose balance does not move is the worst
 * kind of wrong — plausible, specific and false — so the payout is dropped and the
 * ribbon says what actually happened instead. The **rank bar goes with it**, for
 * the same reason and one step further: it would show a promotion segment for XP
 * that is never banked.
 *
 * That is a guard somebody has to remember, which the old arrangement was not,
 * so it is one named computed read by every block rather than several `v-if`s.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import type { HudState } from '@/game/arena-scene';
import { formatMetres, objectiveTally, promotionView } from '@/game/hud-readout';
import { rankArtPath } from '@/garage/rank-ladder';
import { useGarage } from '@/state/garage';
import CurrencyIcon from './CurrencyIcon.vue';
import TitleBadge from './TitleBadge.vue';

const props = defineProps<{
  hud: HudState;
  /**
   * The signed-in player's own call sign.
   *
   * Needed because `publishHud` writes the local player's nickname as the
   * literal "You" — see `board`. Passed down rather than read from a store, so
   * this component stays a pure function of its props the way the rest of the
   * debrief is.
   */
  nickname?: string;
}>();

const rankArtMissing = ref(false);
const rankArt = computed(() => {
  const rank = props.hud.rankAtJoin?.rank;
  if (!rank || rankArtMissing.value) return null;
  return rankArtPath(rank);
});

/**
 * The compact recomposition, keyed off the same fact the rest of the HUD keys
 * off — a touch build (`hud.touch` is absent rather than false on a keyboard
 * build). The debrief must fit one 375px-tall landscape-phone screen, and the
 * fluid `vh` clamps alone cannot buy that: the compact class re-lays the hero
 * plates as inline pills, keeps the payout grid and the scoreboard two-across
 * (the width is there in landscape — it is the *height* that is not), and
 * drops the rank chip whose number the figures line already states.
 */
const compact = computed(() => !!props.hud.touch);
/** On a phone the payout's line-by-line breakdown is folded away until asked for. */
const payoutOpen = ref(false);

/**
 * Whether this battle actually pays.
 *
 * False for exactly one thing — the tutorial — and it is read off the mode
 * rather than off the reward being zero, because a reward *can* legitimately be
 * small and a battle somebody lost in twenty seconds is not a battle that banked
 * nothing. See the file comment.
 */
const banks = computed(() => props.hud.mode !== 'tutorial');

/** This battle's base XP, once the tally exists. Feeds the bar's gained segment. */
const earnedXp = computed(() => (banks.value ? (props.hud.battleTally?.reward.xp ?? 0) : 0));

/**
 * The account's live boosts, off the same garage store the title bar and the
 * shop already read — not a prop, because both are true of every screen this
 * component can appear on: the garage was loaded before the sortie started
 * (there is no route into a battle that skips it), and a boost's own duration
 * is hours to days, so the snapshot taken on the way in is still accurate on
 * the way out. `WalletService.boostMultipliers` is read fresh at the moment a
 * sortie actually banks — this is a preview of that figure, not the figure.
 */
const garage = useGarage();
const { shop } = garage;
/**
 * The fuel card. A sortie spends fuel at the gate, so the garage's own reading
 * is stale the moment this screen opens: ask for a fresh one once, then let the
 * wallet drive the card. It is a courtesy beside the debrief — it sells nothing
 * the shop does not, through the shop's own `refillFuel` and tiers.
 */
const wallet = garage.wallet;
const fuelTiers = computed(() => shop.value?.fuelRefill.tiers ?? []);
const topUpTier = computed(() => fuelTiers.value.find((t) => t.units === 1) ?? null);
const fullTankTier = computed(() => fuelTiers.value.find((t) => t.units === 'full') ?? null);
const showFuelCard = computed(
  () =>
    banks.value &&
    !compact.value &&
    !!wallet.value &&
    wallet.value.fuel < wallet.value.fuelCapacity &&
    (topUpTier.value?.available || fullTankTier.value?.available),
);
const armed = ref<1 | 'full' | null>(null);
let disarm: ReturnType<typeof setTimeout> | null = null;
onMounted(() => void garage.refresh());
onBeforeUnmount(() => {
  if (disarm) clearTimeout(disarm);
});

/** Two taps: the first arms the button and shows the spend, the second buys. */
async function buyFuel(units: 1 | 'full'): Promise<void> {
  if (armed.value !== units) {
    armed.value = units;
    if (disarm) clearTimeout(disarm);
    disarm = setTimeout(() => (armed.value = null), 4000);
    return;
  }
  armed.value = null;
  await garage.refillFuel(units);
}

const activeXpBoost = computed(
  () => shop.value?.boosts.find((b) => b.kind === 'XP' && b.active) ?? null,
);
const activeLootBoost = computed(
  () => shop.value?.boosts.find((b) => b.kind === 'LOOT' && b.active) ?? null,
);

/** A multiplier scaled and capped the same way `boostScale` does server-side. */
function boostScale(multiplier: number): number {
  return Number.isFinite(multiplier) && multiplier >= 1 ? Math.min(multiplier, 10) : 1;
}

/** `earnedXp`, scaled by the active XP boost — what the rank bar actually fills. */
const boostedXp = computed(() =>
  activeXpBoost.value ? Math.round(earnedXp.value * boostScale(activeXpBoost.value.multiplier)) : earnedXp.value,
);

/**
 * The rank bar: the rank the player deployed with, plus the segment this battle
 * just earned — boosted, so the bar and the figure under it agree with what
 * actually banks. Null when the garage could not say.
 */
const promotion = computed(() =>
  banks.value ? promotionView(props.hud.rankAtJoin, boostedXp.value) : null,
);

/** What this mode was counting. Null for anything without an objective. */
const objective = computed(() =>
  objectiveTally({
    mode: props.hud.mode,
    won: props.hud.won,
    mission: props.hud.mission,
    score: props.hud.score,
    localTeam: props.hud.localTeam,
  }),
);

/**
 * The player's own headline figures, as plates under the banner.
 *
 * Kills always — it is on the roster whether or not the tally arrived — and the
 * tally's own three beside it once it exists. Crates only when any were taken:
 * a zero-crate plate is a claim about a mechanic the match may never have
 * offered. Deaths are deliberately absent — they are on the scoreboard below,
 * and a headline row is for what the reward curve pays, not for what it costs.
 */
const heroStats = computed(() => {
  const tally = props.hud.battleTally;
  const out: { key: string; label: string; value: string }[] = [
    { key: 'kills', label: 'Kills', value: String(props.hud.kills ?? 0) },
  ];
  if (tally) {
    out.push({
      key: 'damage',
      label: 'Damage dealt',
      value: String(Math.round(tally.damageDealt)),
    });
    if (tally.cratesCollected > 0) {
      out.push({ key: 'crates', label: 'Crates', value: String(tally.cratesCollected) });
    }
    // formatMetres already picked the unit ("1.2 km" or "850 m"); stating it
    // in the label instead lets the value stay a bare number, matching the
    // other three plates.
    const [drivenValue, drivenUnit] = formatMetres(tally.distanceDriven).split(' ');
    out.push({ key: 'driven', label: `Driven ${drivenUnit}`, value: drivenValue });
  }
  return out;
});

/** One photo per hero stat, replacing the old line-art icon. */
const STAT_ART: Record<string, string> = {
  kills: '/assets/result/stat-kills.webp',
  damage: '/assets/result/stat-damage.webp',
  crates: '/assets/result/stat-crates.webp',
  driven: '/assets/result/stat-driven.webp',
};

interface BoardRow {
  id: string;
  name: string;
  /** The title this player wears, or absent for none. Shown beside the name. */
  titleId?: string;
  isLocal: boolean;
  isBot: boolean;
  kills: number;
  deaths: number;
  /** Null when the summary was missed — unknown, never zero. */
  damage: number | null;
  /** The best line on the whole board, kills first, damage as the tiebreak. */
  top: boolean;
}

/**
 * The scoreboard, one section per side, the player's own first.
 *
 * Sorted by kills then damage, which is the order the reward curve itself pays
 * in. The ★ marks the best line on the whole board — the "best player" the
 * curve was tuned around — and the local row is highlighted whether or not it
 * is that one.
 */
const board = computed(() => {
  const byId = new Map((props.hud.battleSummary ?? []).map((t) => [t.playerId, t]));
  const rows: (BoardRow & { mine: boolean })[] = props.hud.players.map((p) => ({
    id: p.id,
    // `publishHud` writes the local player's nickname as the literal "You",
    // which is right on a live HUD and wrong on a debrief: this is the screen a
    // player reads their own line on beside everybody else's, and a row that
    // says "You" cannot be compared to one that says a call sign, cannot be
    // read out to somebody, and is the only row on the board not naming a
    // person. The real name comes down as a prop, the same one the in-battle
    // roster already substitutes for exactly this reason.
    name: p.isLocal ? props.nickname || p.nickname : p.nickname,
    titleId: p.titleId,
    isLocal: p.isLocal,
    isBot: p.isBot === true,
    mine: p.isLocal || p.friendly === true,
    kills: p.kills ?? 0,
    deaths: p.deaths ?? 0,
    damage: byId.has(p.id) ? Math.round(byId.get(p.id)!.damageDealt) : null,
    top: false,
  }));

  const order = (a: BoardRow, b: BoardRow) =>
    b.kills - a.kills || (b.damage ?? 0) - (a.damage ?? 0) || a.name.localeCompare(b.name);
  const best = [...rows].sort(order)[0];
  if (best && (best.kills > 0 || (best.damage ?? 0) > 0)) best.top = true;

  return [
    { label: 'Your side', rows: rows.filter((r) => r.mine).sort(order) },
    { label: 'Enemy side', rows: rows.filter((r) => !r.mine).sort(order) },
  ].filter((s) => s.rows.length > 0);
});

/** Whether any damage figure is known, so the column is all-or-nothing. */
const hasDamage = computed(() => (props.hud.battleSummary ?? []).length > 0);

/**
 * The reward breakdown, as rows that always sum to the headline — the server
 * rounds each component and totals them, precisely so this list adds up.
 * Zero-value sources are dropped: a "Victory bonus 0" line on a lost battle is
 * a worse way of saying DEFEAT than the banner above already did.
 *
 * Each row carries a `bonus` — the credits the active loot boost would add to
 * that line, rounded the same way the server rounds the total — so the table
 * reads "base, then what the booster adds" rather than a total that moved
 * with no explanation. `credits` is the boosted total to match; the rows plus
 * their bonuses still sum to it.
 */
const rewardRows = computed(() => {
  if (!banks.value) return null;
  const reward = props.hud.battleTally?.reward;
  if (!reward) return null;
  const scale = activeLootBoost.value ? boostScale(activeLootBoost.value.multiplier) : 1;
  const row = (label: string, value: number) => ({
    label,
    value,
    bonus: Math.round(value * (scale - 1)),
  });
  const rows: { label: string; value: number; bonus: number }[] = [
    row('Participation', reward.participation),
  ];
  if (reward.kills > 0) rows.push(row(`Kills × ${props.hud.kills ?? 0}`, reward.kills));
  if (reward.damage > 0) rows.push(row('Damage dealt', reward.damage));
  if (reward.win > 0) rows.push(row('Victory bonus', reward.win));
  return { rows, credits: Math.round(reward.credits * scale), xp: boostedXp.value };
});
</script>

<template>
  <div class="summary" :class="{ compact }" data-test="battle-summary">
    <div v-if="heroStats.length" class="hero" data-test="hero-stats">
      <div v-for="stat in heroStats" :key="stat.key" class="hero-stat" :data-stat="stat.key">
        <div class="hero-art">
          <img
            class="hero-art-img"
            :src="STAT_ART[stat.key]"
            alt=""
            decoding="async"
            loading="lazy"
          />
          <span class="hero-art-tint" aria-hidden="true"></span>
          <span class="hero-art-warmth" aria-hidden="true"></span>
          <span class="hero-art-glare" aria-hidden="true"></span>
        </div>
        <div class="hero-figures">
          <span class="hero-label">{{ stat.label }}</span>
          <span class="hero-value">{{ stat.value }}</span>
        </div>
      </div>
    </div>

    <p v-if="objective" class="objective" data-test="objective-tally">
      <span class="objective-label">{{ objective.label }}</span>
      <span class="objective-value">{{ objective.value }}</span>
    </p>

    <section v-if="!banks || rewardRows || promotion || showFuelCard" class="ribbon" :class="{ muted: !banks }">
      <div class="ribbon-inner" :class="{ 'with-fuel': showFuelCard }">
        <p v-if="!banks" class="unpaid" data-test="unpaid">
          Basic training pays nothing — no credits, no XP, no rank. It costs nothing
          either. Your record shows the drills as complete.
        </p>

        <div v-if="rewardRows" class="ribbon-grid" data-test="rewards">
          <div class="breakdown">
            <button
              v-if="compact"
              type="button"
              class="summary-label payout-toggle"
              data-test="payout-toggle"
              :aria-expanded="payoutOpen"
              @click="payoutOpen = !payoutOpen"
            >
              Sortie payout
              <svg class="chevron" :class="{ open: payoutOpen }" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M3 4.5l3 3 3-3" />
              </svg>
            </button>
            <span v-else class="summary-label">Sortie payout</span>
            <template v-if="!compact || payoutOpen">
            <!--
              The header row is its own element sharing `.summary-rows`'s grid
              template — the same split `.board-head`/`.board-grid` uses below —
              rather than a row inside the `dl`, so "Base"/"Booster" can be
              skipped entirely (not just left blank) when nothing is boosted.
            -->
            <div v-if="activeLootBoost" class="summary-rows summary-rows-head boosted" aria-hidden="true">
              <span></span>
              <span class="col-head">Base</span>
              <span class="col-head">Booster</span>
            </div>
            <dl class="summary-rows" :class="{ boosted: !!activeLootBoost }">
              <template v-for="row in rewardRows.rows" :key="row.label">
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
                <dd v-if="activeLootBoost" class="bonus">+{{ row.bonus }}</dd>
              </template>
            </dl>
            </template>
          </div>
          <div class="reward-totals">
            <div class="reward-credits-block">
              <span class="reward-total credits" data-test="reward-credits">
                <span class="reward-total-label">Total:</span> +{{ rewardRows.credits }}
                <CurrencyIcon kind="credits" />
              </span>
            </div>
            <!--
              The XP figure and the bar it fills are one fact, so on a full-size
              debrief it lives under the bar instead — see `.promotion-gain`.
              It stays here in two cases:

              **No bar to join.** A debrief with no `rankAtJoin` gets no
              `promotion` block at all, and the figure must not vanish with it.

              **Compact.** A landscape phone puts the payout and the rank block
              side by side in one ribbon row, so the rank column is narrow and
              the figure wraps the "promoted when this sortie banks" line onto a
              second — 13px on a 375px-tall screen, which `result-fit` counts as
              a scoreboard row lost. Compact already puts the two within a
              column of each other, which is what the move was for.
            -->
            <span v-if="!promotion" class="reward-total xp" data-test="reward-xp">
              +{{ rewardRows.xp }} <small>xp</small>
            </span>
          </div>
        </div>

        <div v-if="promotion" class="promotion" data-test="promotion" :data-rank="promotion.rank">
          <div class="rank-disc" data-test="promotion-rank-disc">
            <img
              v-if="rankArt"
              class="rank-art"
              :src="rankArt"
              alt=""
              data-test="promotion-rank-art"
              decoding="async"
              @error="rankArtMissing = true"
            />
            <span v-else class="rank-plate">{{ promotion.rank }}</span>
          </div>
          <div class="promotion-body">
            <div class="promotion-head">
              <span class="summary-label">Rank progress</span>
              <span class="promotion-rank">{{ promotion.title }}</span>
              <!-- Touch: the XP gained sits on the right, over the bar it fills. -->
              <span v-if="rewardRows && compact" class="reward-total xp head-xp" data-test="reward-xp">
                +{{ rewardRows.xp }} <small>xp</small>
              </span>
            </div>
            <span class="promotion-bar" aria-hidden="true">
              <i :style="{ width: `${promotion.fraction * 100}%` }"></i>
              <i
                v-if="promotion.gainedFraction > 0"
                class="gained"
                :style="{
                  left: `${promotion.fraction * 100}%`,
                  width: `${promotion.gainedFraction * 100}%`,
                }"
              ></i>
            </span>
            <!--
              Two columns, not a wrapping run: what happened on the left, what
              it paid on the right, at the end of the row the way a receipt's
              own total sits. `justify-content: space-between` is what buys
              that with exactly two children — no explicit grid needed.
            -->
            <div class="promotion-gain">
              <span v-if="promotion.atTop" class="promotion-figures" data-test="promotion-figures">
                Rank {{ promotion.rank }} · top of the ladder
              </span>
              <span
                v-else-if="promotion.promoted"
                class="promotion-figures"
                data-test="promotion-figures"
              >
                Rank {{ promotion.rank + 1 }} reached — promoted when this sortie banks
              </span>
              <span v-else class="promotion-figures" data-test="promotion-figures">
                {{ promotion.xpIntoRank }} + {{ boostedXp }} / {{ promotion.xpForNextRank }} xp to
                rank {{ promotion.rank + 1 }}
              </span>
              <span v-if="rewardRows && !compact" class="reward-total xp" data-test="reward-xp">
                +{{ rewardRows.xp }} <small>xp</small>
              </span>
            </div>
            <!--
              A status, not a disclaimer: the old text ("active boosts are
              added when the sortie banks") was hedging against a figure this
              screen could not see. It can now — `boostedXp` already is that
              figure — so there is nothing left to caveat, and the note says
              so only when it is actually true.
            -->
            <p v-if="activeXpBoost" class="promotion-note" data-test="promotion-note">
              Boosted with {{ activeXpBoost.name }}
            </p>
          </div>
        </div>
        <aside v-if="showFuelCard" class="fuel-card" data-test="fuel-card">
          <img class="fuel-art" src="/assets/store/fuel-refill.webp" alt="" decoding="async" />
          <div class="fuel-copy">
            <span class="summary-label">Fuel</span>
            <span class="fuel-level" data-test="fuel-card-level">
              {{ wallet!.fuel }} / {{ wallet!.fuelCapacity }}
            </span>
          </div>
          <div class="fuel-actions">
            <button
              v-if="fullTankTier?.available"
              type="button"
              class="fuel-btn primary"
              :class="{ armed: armed === 'full' }"
              data-test="fuel-card-full"
              :disabled="garage.busyId.value !== null || wallet!.diamonds < fullTankTier.diamondCost"
              @click="buyFuel('full')"
            >
              <span class="fuel-btn-name">{{ armed === 'full' ? 'Tap to confirm' : 'Full tank' }}</span>
              <span class="fuel-btn-price">{{ fullTankTier.diamondCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></span>
            </button>
            <button
              v-if="topUpTier?.available"
              type="button"
              class="fuel-btn"
              :class="{ armed: armed === 1 }"
              data-test="fuel-card-plus-one"
              :disabled="garage.busyId.value !== null || wallet!.diamonds < topUpTier.diamondCost"
              @click="buyFuel(1)"
            >
              <span class="fuel-btn-name">{{ armed === 1 ? 'Tap to confirm' : '+1 fuel' }}</span>
              <span class="fuel-btn-price">{{ topUpTier.diamondCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></span>
            </button>
          </div>
        </aside>
      </div>
    </section>

    <div v-if="board.length" class="board" data-test="result-board">
      <section
        v-for="side in board"
        :key="side.label"
        class="board-side"
        :class="{ 'no-dmg': !hasDamage }"
      >
        <header class="board-grid board-head">
          <span class="summary-label">{{ side.label }}</span>
          <span class="board-col" aria-hidden="true">K</span>
          <span class="board-col" aria-hidden="true">D</span>
          <span v-if="hasDamage" class="board-col" aria-hidden="true">DMG</span>
        </header>
        <ol class="board-rows">
          <li
            v-for="row in side.rows"
            :key="row.id"
            class="board-grid board-row"
            :class="{ local: row.isLocal }"
            :data-test="row.isLocal ? 'board-row-local' : undefined"
          >
            <span class="board-name">
              <span v-if="row.top" class="board-star" title="Best player">★</span>
              <TitleBadge v-if="row.titleId" :title-id="row.titleId" variant="feed" />
              <span class="board-who">{{ row.name }}</span>
            </span>
            <span class="board-num">{{ row.kills }}</span>
            <span class="board-num">{{ row.deaths }}</span>
            <span v-if="hasDamage" class="board-num">{{ row.damage ?? '—' }}</span>
          </li>
        </ol>
      </section>
    </div>

  </div>
</template>

<style scoped>
/*
 * The body of a full-screen debrief, not a HUD panel: no border and no card of
 * its own, because `GameHud`'s `.result` owns the scrim and the scroll. The one
 * layout job here is letting the ribbon run the full viewport width while the
 * tables stay on the shared measure.
 */
.summary {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Breathes with the viewport's height — the one-screen rule stated on
     `.result-col`, whose pressure this file shares. */
  gap: clamp(0.55rem, 1.6vh, 1.4rem);
  /* The flex chain from `.result-col`: when the column is at the viewport's
     cap, the scoreboard is the one block that gives way (its rows scroll
     within themselves); the plates and the ribbon never shrink. */
  min-height: 0;
  /* Overrides the banner area's uppercase tracking, which is right for a
     headline and wrong for tables of figures and sentences. */
  text-transform: none;
  letter-spacing: normal;
}

.summary-label {
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400);
}

/* The shared measure every non-ribbon block sits on. */
.hero,
.board {
  width: min(1060px, 92vw);
}

/* ─── Personal figures ───────────────────────────────────────────────────── */

.hero {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.9rem;
  flex: none;
}

/*
 * The four figures a player actually came to read, so they are framed like a
 * result and not like a table cell.
 *
 * The border is a static brushed-metal gradient — pale highlight to bronze to
 * a dark near-black — held still rather than travelling the way
 * ResearchTree.vue's `.node.running` border does for "this is progressing":
 * a debrief is a fact, not a process. Painted with the two-layer background
 * trick (fill on the padding box, the gradient on the border box) because a
 * plain `border` can't take a gradient.
 *
 * The card is sized to its own content rather than to a round number: the
 * `clamp` is tight enough that "Damage dealt" — the longest label — wraps to
 * two lines rather than forcing every card wider to fit one line of it.
 */
.hero-stat {
  position: relative;
  min-width: clamp(8rem, 14vw, 8.4rem);
  height: clamp(3.4rem, 8.5vh, 4.3rem);
  display: flex;
  align-items: stretch;
  border: 1.5px solid transparent;
  border-radius: 5px;
  background:
    linear-gradient(180deg, rgba(18, 15, 11, 0.94), rgba(10, 8, 6, 0.97)) padding-box,
    linear-gradient(135deg, #beb9b2, #6b4718 55%, #382d22) border-box;
  overflow: hidden;
}

/*
 * The left portion: a photo standing in for the old line-art icon, cut on a
 * shallow diagonal rather than a plumb vertical edge so the card reads as a
 * cut ribbon rather than a table cell split in two.
 *
 * The clip-path lives here, on the container, rather than repeated on each
 * of the four layers inside it — that's what lets the image itself carry a
 * per-stat `transform: scale()` (below) without the cut moving with it: the
 * window is fixed, only what's framed through it zooms.
 *
 * Widened to 65% of the card so the photo carries most of it — the figures
 * column is deliberately the smaller half now.
 */
.hero-art {
  position: relative;
  flex: none;
  width: 65%;
  clip-path: polygon(0 0, 85% 0, 73% 100%, 0 100%);
}

/*
 * Muted and darkened so this reads as a mounted illustration rather than a
 * crisp photograph dropped into a UI — desaturated and dimmed on the image
 * itself, `.hero-art-tint` and `.hero-art-warmth` below shading it further.
 * `drop-shadow` (not `box-shadow`, which a `clip-path` doesn't bend) follows
 * the clipped silhouette exactly, so the cut edge casts a soft, graduated
 * shade rather than the hard second outline a doubled clipped shape used to
 * draw.
 *
 * Every source photo ships as a full, uncropped resize — nothing was cut at
 * import — so `object-position` has the whole frame to draw from rather than
 * whatever survived a square crop taken before it reached here.
 */
.hero-art-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: right center;
  filter: grayscale(0.3) brightness(0.78) contrast(1.1) saturate(0.9)
    drop-shadow(3px 0 6px rgba(0, 0, 0, 0.65));
}

/* Per-photo framing — where `.hero-art`'s clip-path draws the line, each
   photo needs its own zoom and offset to land its subject inside it. */
.hero-stat[data-stat='kills'] .hero-art-img {
  transform: scale(2.2);
}

.hero-stat[data-stat='damage'] .hero-art-img {
  transform: scale(1.4);
  object-position: -5px -10px;
}

.hero-stat[data-stat='crates'] .hero-art-img {
  transform: scale(1.5);
  object-position: -21px -3px;
}

/* A darker fade toward the cut — the tint that keeps this from reading as a
   photograph, on top of the desaturation already in the image's own filter. */
.hero-art-tint {
  position: absolute;
  inset: 0;
  background: linear-gradient(303deg, rgb(71 53 35 / 39%) 25%, rgb(8 6 3 / 79%) 100%);
  pointer-events: none;
}

/* A second, warmer wash the other way — a hint of colour back into the
   corner the dark tint above leaves flattest, rather than one uniform fade. */
.hero-art-warmth {
  position: absolute;
  inset: 0;
  background: linear-gradient(275deg, rgb(140 114 88 / 18%) 25%, rgb(0 0 0 / 0%) 41%);
  pointer-events: none;
}

/* A very slight gloss, not a highlight — a photo behind glass, not a sticker. */
.hero-art-glare {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0) 40%,
    rgba(255, 255, 255, 0.07) 48%,
    rgba(255, 255, 255, 0) 56%
  );
  pointer-events: none;
}

/* The right portion: label directly over the value, both anchored to the
   same right edge, the pair centred as one block against the card's height —
   opposing the diagonal photo with the plainest possible figure. Deliberately
   the smaller half now that the photo is 65% of the card. */
.hero-figures {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 0.15rem;
  padding: 0 0.6rem 0 0.3rem;
}

.hero-value {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 1.1rem;
  line-height: 1.1;
  color: #fff;
  white-space: nowrap;
}

/*
 * `--steel-400` measured **3.76:1** against this panel — under WCAG AA's 4.5:1
 * for text this size, and it is 8px uppercase, which is the hardest thing on
 * the screen to read even at full contrast. `--steel-200` is 10.03:1. The
 * label is half of what a figure means; "14" and "14 KILLS" are not the same
 * readout.
 *
 * `pre-wrap` rather than `nowrap`: the figures column is now the smaller half
 * of a narrow card, and "Damage dealt" no longer fits it on one line.
 * `text-align: right` is what keeps a wrapped second line flush with the
 * first — `align-items: flex-end` on the parent only places the block as a
 * whole, not each line inside it.
 */
.hero-label {
  font-size: 8px;
  line-height: 1.15;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: right;
  color: var(--steel-200);
  white-space: pre-wrap;
}

/* ─── Objective line ─────────────────────────────────────────────────────── */

.objective {
  flex: none;
  margin: -0.4rem 0 0;
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  font-size: 0.78rem;
  color: var(--steel-200);
}

.objective-label {
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.objective-value {
  font-family: ui-monospace, 'SF Mono', monospace;
}

/* ─── The payout ribbon ──────────────────────────────────────────────────────
 *
 * Full-bleed on purpose — the one loud element on the screen, so everything
 * else stays quiet steel. The diagonal striping is the game's own hazard value
 * structure (light against dark on the diagonal), kept faint enough that the
 * figures stay the brightest thing on the band.
 *
 * **It is near-opaque, and that changed when the screen got a painting behind
 * it.** These alphas were 0.3–0.5, tuned when `.result` was a flat near-opaque
 * scrim and letting a little of it through cost nothing. Over
 * `/assets/result/*.webp` the same values turn the band into a *window*: a gun
 * barrel and a stretch of sky read straight through the striping, which is the
 * one thing the ribbon must not do — it is the payout's identity, so it sits
 * **on** the picture rather than showing it.
 *
 * The colours are the *composite* the old translucent values produced over the
 * old near-black scrim, not the same literals turned opaque. That distinction is
 * the whole care of it: `rgba(168, 80, 27, 0.3)` over `#090b0f` resolves to about
 * `rgb(57, 32, 19)`, and simply raising the alpha on the original 168/80/27
 * makes the band three times brighter — a much louder ribbon than anybody asked
 * for, arriving as a side effect of a backdrop change. So the band looks exactly
 * as it did; it just no longer has a picture behind it.
 */
.ribbon {
  flex: none;
  width: 100%;
  border-top: 1px solid rgba(232, 114, 44, 0.55);
  border-bottom: 1px solid rgba(232, 114, 44, 0.3);
  background:
    repeating-linear-gradient(
      -45deg,
      rgba(232, 114, 44, 0.055) 0 16px,
      rgba(0, 0, 0, 0) 16px 32px
    ),
    linear-gradient(180deg, rgba(57, 32, 19, 0.97), rgba(36, 21, 14, 0.975) 55%, rgba(17, 13, 12, 0.98));
  box-shadow:
    inset 0 1px 0 rgba(255, 201, 138, 0.18),
    inset 0 0 46px rgba(232, 114, 44, 0.1);
}

/* The tutorial's band: same place, no payout, so no ember. Opaque for the same
   reason as the ember one — a muted band is still a band, not a window. */
.ribbon.muted {
  border-color: var(--steel-600);
  background: linear-gradient(180deg, rgba(38, 46, 58, 0.94), rgba(24, 30, 38, 0.96));
  box-shadow: none;
}

.ribbon-inner {
  width: min(1060px, 92vw);
  margin: 0 auto;
  padding: clamp(0.55rem, 1.6vh, 1.3rem) 0 clamp(0.5rem, 1.4vh, 1.15rem);
  display: grid;
  gap: clamp(0.5rem, 1.3vh, 1.1rem);
}

.unpaid {
  margin: 0;
  padding: 0.2rem 0;
  color: var(--steel-200);
  font-size: 0.84rem;
  line-height: 1.5;
  text-align: center;
}

.ribbon-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.8rem 2.5rem;
}

@media (max-width: 700px) {
  .ribbon-grid {
    grid-template-columns: 1fr;
  }
}

.breakdown {
  display: grid;
  gap: 0.3rem;
  max-width: 20rem;
}

/*
 * A two-column definition list: labels dim on the left, figures light and
 * monospaced on the right. Monospaced so the column of numbers lines up down its
 * own right edge — the same reason the roster's kills/deaths column does.
 *
 * `.boosted` adds the third: what the active loot boost adds to that line,
 * absolute rather than a percentage, because the multiplier is already named
 * once (the boost's own name, on the rank panel) and a row of numbers is
 * read faster than a row of multiplications.
 */
.summary-rows {
  margin: 0;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: clamp(0.05rem, 0.3vh, 0.24rem) 1rem;
  align-items: baseline;
}

.summary-rows.boosted {
  grid-template-columns: 1fr auto auto;
}

/* Shares the row grid rather than living inside the `dl` — the same split
   `.board-head`/`.board-grid` uses for the scoreboard below. */
.summary-rows-head {
  margin-bottom: 0.05rem;
}

.summary-rows-head .col-head {
  font-size: 0.56rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--steel-400);
  text-align: right;
}

.summary-rows dt {
  font-size: 0.74rem;
  color: var(--steel-200);
}

.summary-rows dd {
  margin: 0;
  font-size: 0.8rem;
  color: #fff;
  font-family: ui-monospace, 'SF Mono', monospace;
  text-align: right;
}

.summary-rows dd.bonus {
  color: #ffc98a;
}

.reward-totals {
  display: grid;
  justify-items: end;
  align-items: center;
  gap: 0.8rem;
}

.reward-credits-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.reward-total {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: clamp(1.6rem, 3vw, 2.35rem);
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.55);
}

.reward-total small {
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--steel-200);
}

.reward-total.credits {
  color: var(--accent);
  font-size: 2.55rem;
}

/*
 * "Total", inline with the figure rather than stacked above it as its own
 * `.summary-label` ("Earned") used to be — a breakdown with only one line
 * (a forfeit's "Participation" alone, say) has that one row's value equal
 * this figure exactly, and a label reading "Earned" over an identical number
 * a few centimetres to its left reads as the same fact stated twice rather
 * than as a sum. "Total" is correct even then — one row is still a sum, of
 * one term — so naming it that instead resolves the look of redundancy
 * without having to special-case the single-row breakdown at all.
 *
 * Styled as part of the figure it precedes rather than as a caption on it —
 * same monospace, same accent colour, only a shade dimmer and a size down —
 * so "Total: +443" reads as one statement instead of a small grey label
 * sitting beside an unrelated-looking large one.
 */
.reward-total-label {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--accent);
  opacity: 0.82;
  vertical-align: 0.14em;
}

/* XP in the same warm tone as the bar segment it becomes, so the total and the
   gained run on the rank bar read as one fact. */
.reward-total.xp {
  color: #ffc98a;
  font-size: clamp(1.05rem, 2vw, 1.35rem);
}

/* ─── Promotion ──────────────────────────────────────────────────────────── */

.promotion {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 1.1rem;
  padding-top: clamp(0.45rem, 1.1vh, 0.9rem);
  border-top: 1px solid rgba(232, 114, 44, 0.22);
}

.ribbon.muted .promotion {
  border-top-color: var(--steel-700);
}

/*
 * The same disc-and-overflowing-art badge the profile card uses (see
 * `AccountPane.vue`'s `.rank-disc`/`.rank-art`), sized to the old square
 * chip's own footprint (3.2rem) rather than the card's full-size 88px —
 * this sits in a ribbon that already has a payout table and a progress bar
 * to fit above the fold, and the card's size cost the debrief its one-screen
 * guarantee at a 1280×660 laptop window (`result-fit`) the one time it was
 * tried. `overflow: visible` on the art is what buys the "art bigger than
 * its ring" relief at this size too — the effect does not need the size.
 */
.rank-disc {
  position: relative;
  width: 3.2rem;
  height: 3.2rem;
  flex: none;
  border-radius: 50%;
  border: 2px solid var(--accent);
  background: radial-gradient(circle at 50% 40%, #2a1a0c 0%, #0d1014 78%);
  box-shadow: 0 0 14px rgba(232, 114, 44, 0.22);
}

.rank-art {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 4.1rem;
  transform: translate(-50%, -52%);
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.55));
  transition: transform 200ms cubic-bezier(0.2, 0.7, 0.3, 1);
}

.rank-plate {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-family: var(--font-display);
  font-size: 1.25rem;
  color: var(--accent);
}

.rank-disc:hover .rank-art {
  transform: translate(-50%, -57%) scale(1.05);
}

.promotion-body {
  display: grid;
  gap: clamp(0.18rem, 0.5vh, 0.4rem);
}

.promotion-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.promotion-rank {
  font-size: 0.92rem;
  color: #fff;
  letter-spacing: 0.05em;
}

/*
 * Drawn in CSS at whatever the device pixel ratio is, like every other bar and
 * badge on this HUD — see the note in `TitleBadge.vue` on why nothing here is an
 * image. The insignia art belongs in the garage's rank window at full size; at
 * this height chevrons are countable by nobody.
 */
.promotion-bar {
  position: relative;
  display: block;
  height: 8px;
  border-radius: 4px;
  background: rgba(11, 13, 17, 0.7);
  overflow: hidden;
}

.promotion-bar i {
  position: absolute;
  inset: 0 auto 0 0;
  /* No transition. These values change once, when the screen appears; easing
     them would animate a bar toward figures that are already final. */
  background: linear-gradient(90deg, var(--accent-dim), var(--accent));
}

/* The battle's own segment, brighter than the run it extends so the evening's
   work is visible on the bar rather than only in the figures. */
.promotion-bar i.gained {
  inset: 0 auto 0 auto;
  background: #ffc98a;
}

/*
 * What this sortie earned, and what it did to the bar, on one line under it.
 *
 * The XP total used to sit up in the payout column beside the credits, which is
 * where it belongs arithmetically — both are things the sortie paid — and not
 * where it belongs to a reader: the bar directly below was the *same number*
 * being spent, and the two were a column apart with a breakdown table between
 * them. Credits stay up there because nothing on this screen draws them; XP
 * comes down to the thing it fills.
 *
 * Two columns, not a run of baseline-aligned spans: what happened reads on
 * the left, what it paid sits at the end on the right, the way a receipt's
 * own total does. `space-between` gets there with exactly the two children
 * this row ever has, no explicit grid needed — and still wraps the long
 * "promoted when this sortie banks" line onto its own row rather than
 * squeezing the figure.
 */
.promotion-gain {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.15rem 0.7rem;
}

.promotion-figures {
  font-size: 0.72rem;
  color: var(--steel-200);
  font-family: ui-monospace, 'SF Mono', monospace;
}

/*
 * Smaller here than in the payout column it came from: beside a sentence it is
 * a figure on a line, not the headline of a block. Clamped on `vh` like every
 * other vertical on this screen — `vw` would grow it on a wide-but-short laptop
 * window, which is the exact shape `result-fit` is hardest on.
 */
.promotion-gain .reward-total.xp {
  font-size: clamp(0.85rem, 1.9vh, 1.35rem);
  /* Flush with the figures line it now shares a row with: at the clamp's floor
     this is 13.6px against their 13.8px, so joining them costs the row nothing.
     `result-fit` fails by 2px if it does. */
  line-height: 1;
}

.promotion-note {
  margin: 0.1rem 0 0;
  font-size: 0.66rem;
  line-height: 1.45;
  color: var(--steel-400);
}

/* ─── Fuel top-up ────────────────────────────────────────────────────────── */

/* A narrow column at the ribbon's right, spanning the payout and the rank row. */
.ribbon-inner.with-fuel {
  grid-template-columns: minmax(0, 1fr) 9.25rem;
  column-gap: 1.6rem;
}

.ribbon-inner.with-fuel > .ribbon-grid,
.ribbon-inner.with-fuel > .promotion {
  grid-column: 1;
}

.fuel-card {
  position: relative;
  isolation: isolate;
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0 0.5rem 0.5rem;
  border: 1px solid rgba(255, 150, 70, 0.6);
  border-radius: 6px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0) 30%),
    linear-gradient(180deg, rgba(40, 24, 15, 0.95), rgba(12, 10, 10, 0.97));
  box-shadow:
    inset 0 1px 0 rgba(255, 190, 130, 0.35),
    inset 0 -0.7rem 1rem rgba(0, 0, 0, 0.35),
    0 0.6rem 1.2rem rgba(0, 0, 0, 0.55);
}

/* The attention glow: a halo that breathes, on its own layer so only opacity animates. */
.fuel-card::before {
  content: '';
  position: absolute;
  inset: -1px;
  z-index: -1;
  border-radius: inherit;
  box-shadow:
    0 0 1rem 0.1rem rgba(232, 114, 44, 0.6),
    0 0 2.4rem 0.3rem rgba(232, 114, 44, 0.3);
  opacity: 0.55;
  animation: fuel-glow 2.6s ease-in-out infinite;
  pointer-events: none;
}

.fuel-card:hover::before {
  opacity: 1;
  animation: none;
}

/* The product photo fills the card's head edge to edge and fades into the body. */
.fuel-art {
  display: block;
  width: calc(100% + 1rem);
  margin: -1px -0.5rem 0;
  height: 4.8rem;
  object-fit: cover;
  object-position: 50% 45%;
  border-radius: 5px 5px 0 0;
  -webkit-mask-image: linear-gradient(180deg, #000 70%, transparent);
  mask-image: linear-gradient(180deg, #000 70%, transparent);
  pointer-events: none;
}

.fuel-copy {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: -0.7rem;
  padding: 0 0.15rem;
  position: relative;
}

.fuel-level {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

.fuel-actions {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.fuel-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--steel-400, #78838f);
  border-radius: 4px;
  background: linear-gradient(180deg, #3b4552, #232a33);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 3px 0 #11151a,
    0 0.5rem 0.8rem rgba(0, 0, 0, 0.45);
  color: #fff;
  font: inherit;
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease,
    filter 0.12s ease,
    border-color 0.12s ease;
}

/* The full tank is the offer: bigger, hotter, with a sheen crossing it. */
.fuel-btn.primary {
  transition-duration: 0.22s;
  min-height: 3.3rem;
  padding: 0.3rem 0.5rem;
  border-color: #ffc489;
  /* A fill, not a gradient: style.css lays the steel texture over `background-color`. */
  background: #f07a30;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    0 4px 0 #7a3410,
    0 0.6rem 1.4rem rgba(232, 114, 44, 0.55);
  color: #1a0c04;
  overflow: hidden;
}

.fuel-btn.primary .fuel-btn-name {
  font-size: 1rem;
}

.fuel-btn.primary::after {
  content: '';
  position: absolute;
  inset: 0 auto 0 -60%;
  width: 40%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  transform: skewX(-18deg);
  animation: fuel-sheen 3.6s ease-in-out infinite;
  pointer-events: none;
}

.fuel-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: var(--accent);
  color: #fff;
  filter: brightness(1.18);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 5px 0 #11151a,
    0 0.7rem 1.2rem rgba(232, 114, 44, 0.4);
}

.fuel-btn.primary:hover:not(:disabled) {
  color: #1a0c04;
  border-color: #fff;
  /* Halation: light bleeding off the lit edge, hot white to orange to a wide red fringe. */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 0 0.9rem rgba(255, 235, 200, 0.55),
    0 6px 0 #7a3410,
    0 0 0.6rem 0.1rem rgba(255, 220, 170, 0.85),
    0 0 1.6rem 0.4rem rgba(255, 140, 60, 0.7),
    0 0 3.4rem 1rem rgba(232, 80, 30, 0.45);
  text-shadow: 0 0 0.5rem rgba(255, 245, 220, 0.9);
}

.fuel-btn:active:not(:disabled) {
  transform: translateY(2px);
  box-shadow:
    inset 0 2px 0 rgba(0, 0, 0, 0.3),
    0 1px 0 #11151a;
}

.fuel-btn.armed {
  outline: 2px solid rgba(255, 255, 255, 0.9);
  outline-offset: 2px;
}

.fuel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fuel-btn-name {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.fuel-btn-price {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

@keyframes fuel-glow {
  50% {
    opacity: 1;
  }
}

@keyframes fuel-sheen {
  0%,
  55% {
    left: -60%;
  }
  100% {
    left: 130%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fuel-card::before,
  .fuel-btn.primary::after {
    animation: none;
  }
}

@media (max-width: 700px) {
  .ribbon-inner.with-fuel {
    grid-template-columns: 1fr;
  }

  .ribbon-inner.with-fuel > .fuel-card {
    grid-column: 1;
    grid-row: auto;
    width: 9.25rem;
    justify-self: center;
  }
}

/* ─── Scoreboard ─────────────────────────────────────────────────────────── */

.board {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.1rem;
  min-height: 0;
}

/* One column when the screen is squeezed — two crushed tables read worse than
   one scrollable pair. */
@media (max-width: 700px) {
  .board {
    grid-template-columns: 1fr;
  }
}

/* A lone side — the tutorial's drill yard — centres rather than sitting beside
   an empty column that reads as a missing enemy table. */
.board-side:only-child {
  grid-column: 1 / -1;
  justify-self: center;
  width: min(560px, 100%);
}

.board-side {
  /* No horizontal component here — `.board-head` and `.board-row` carry their
     own 0.9rem inset instead, so a row's own box already reaches this card's
     true edge. The bleed used to be a negative margin pulling `.board-row`
     out past this padding, which `.board-rows`' `overflow-x: hidden` (below)
     clipped straight back — the highlight never actually reached the edge,
     it only computed as if it had. */
  padding: clamp(0.4rem, 0.9vh, 0.7rem) 0 clamp(0.45rem, 1vh, 0.8rem);
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: rgba(18, 22, 28, 0.55);
  display: grid;
  /* The header holds its line; the rows track is the one that can be squeezed
     by the chain above, which is what lets the rows scroll inside it. */
  grid-template-rows: auto minmax(0, 1fr);
  align-content: start;
  min-height: 0;
  gap: clamp(0.2rem, 0.45vh, 0.35rem);
}

/*
 * One grid for the header and every row, so the columns are columns.
 *
 * `align-items: center`, not `baseline`. `.board-row` is a grid item in
 * `.board-rows` and stretches to fill its own row track by default — which is
 * wanted, since it is what keeps both sides' tables the same overall height
 * when one side has fewer players than the other (three rows sharing a
 * four-row table's height, say). Baseline alignment anchors this row's own
 * content near the top of that stretched box rather than centering it in it,
 * so the fewer-rows side read as top-heavy against the evenly-spaced other
 * side. Centering is the one alignment that looks right whether the row's box
 * matches its content exactly or is stretched well past it.
 */
.board-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 2.4rem 2.4rem 4rem;
  align-items: center;
  gap: 0.5rem;
}

.board-side.no-dmg .board-grid {
  grid-template-columns: minmax(0, 1fr) 2.4rem 2.4rem;
}

.board-head {
  padding: 0 0.9rem 0.3rem;
  border-bottom: 1px solid var(--steel-700);
}

.board-col {
  font-size: 0.58rem;
  letter-spacing: 0.1em;
  color: var(--steel-400);
  font-family: ui-monospace, 'SF Mono', monospace;
  text-align: right;
}

.board-rows {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.16rem;
  /*
   * The one sanctioned inner scroll on the debrief. A Karsten Basin roster is
   * fifteen a side, and thirty rows fit no screen at any type size — so the
   * *screen* never scrolls (the one-screen rule) and a roster taller than the
   * room the flex chain leaves scrolls inside its own table instead. There is
   * deliberately no viewport-fraction cap here: "whatever is left" comes down
   * from `.result-col`'s `max-height`, so a 4v4 board never scrolls anywhere
   * and a 15v15 board scrolls exactly where the screen ran out. The floor
   * keeps a couple of rows visible even at the tightest squeeze, so the table
   * always reads as a table.
   */
  min-height: 2.9rem;
  overflow-y: auto;
  /* Setting only `overflow-y` leaves `overflow-x` computed as `auto` too, per
     the CSS Overflow spec — so a row a pixel over its column width picked up a
     horizontal scrollbar nobody wanted. The columns already handle their own
     width (`.board-name` truncates, `.board-num` is a fixed column), so the
     axis this table actually needs is the only one it should have. */
  overflow-x: hidden;
}

.board-row {
  /* `.board-side` carries no horizontal padding of its own (see its rule
     above), so this 0.9rem is the row's whole inset from the card edge —
     the highlight paints on this element's own border box, which now *is*
     the card's full width, with no bleed or negative margin needed. */
  padding: 0.16rem 0.9rem;
  border-radius: 2px;
  font-size: 0.78rem;
  color: var(--steel-200);
}

.board-row.local {
  background: rgba(232, 114, 44, 0.14);
  color: #fff;
}

/*
 * A flex row rather than a text run, because it holds up to three things — the
 * best-player star, a title badge and the call sign — and only the last of them
 * may take the ellipsis. As plain inline content the badge butted straight into
 * the name ("WARHOUNDKestrel") and `text-overflow` applied to the whole run, so
 * a long name could truncate the badge instead of itself.
 */
.board-name {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  min-width: 0;
  overflow: hidden;
}

/* The only part that gives way. */
.board-who {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.board-star {
  color: var(--accent);
  flex: none;
}

.board-num {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.74rem;
  color: #e8eef5;
  text-align: right;
}

/*
 * ─── Compact: the debrief on a phone held sideways ───────────────────────────
 *
 * One screen, 375px tall, everything visible — see `compact` in the script for
 * why this is a class off `hud.touch` rather than a media query. The moves,
 * in order of what they buy:
 *
 *   **The hero plates become inline pills.** A stacked icon/value/label plate
 *   is ~90px tall for one figure; a pill carries the same three side by side in
 *   ~30px, and four pills still fit a 667px row with room over.
 *
 *   **The two `max-width: 700px` one-column fallbacks are overridden back to
 *   two columns.** They are *width* accommodations, written for a narrow
 *   portrait window — a landscape phone is short, not narrow, so stacking the
 *   payout under the totals and one side's table under the other's spends the
 *   scarce axis to relieve the plentiful one. Class beats media query on
 *   specificity, so order does not matter.
 *
 *   **The rank chip is dropped.** Its number is already in the figures line
 *   ("… xp to rank 8"), and 51px of decorated square is a third of a pill row.
 *
 * Everything else is the same content two sizes down — nothing informational
 * is hidden, which is the line this screen must not cross: it is the payout's
 * only statement.
 */
.summary.compact {
  gap: 0.3rem;
}

.summary.compact .hero {
  gap: 0.45rem;
}

.summary.compact .hero-stat {
  min-width: 0;
  height: auto;
  align-items: center;
  gap: 0.4rem;
  padding: 0.22rem 0.6rem;
  border-width: 1px;
}

/* The diagonal ribbon treatment doesn't survive pill scale — a plain rounded
   thumbnail carries the same photo in the ~17px this row has to spend, so the
   clip-path that cuts the desktop card's diagonal is cancelled here too. */
.summary.compact .hero-art {
  width: 1.05rem;
  height: 1.05rem;
  border-radius: 3px;
  clip-path: none;
  overflow: hidden;
}

.summary.compact .hero-art-tint,
.summary.compact .hero-art-warmth,
.summary.compact .hero-art-glare {
  display: none;
}

.summary.compact .hero-art-img {
  position: static;
  /* The desktop layer's per-photo zoom/offset and darken-and-desaturate
     treatment are both tuned for a photo the size of a card's own half; at a
     17px thumbnail the first crops it further for no reason and the second
     just reads as murky. */
  transform: none;
  object-position: right center;
  filter: none;
}

/* Back to one row — label and value share the baseline the way an inline
   pill reads ("14 KILLS"), rather than the desktop's stacked column. */
.summary.compact .hero-figures {
  flex-direction: row;
  align-items: baseline;
  gap: 0.35rem;
  padding: 0;
}

.summary.compact .hero-value {
  order: 1;
  font-size: 0.95rem;
}

.summary.compact .hero-label {
  order: 2;
}

.summary.compact .objective {
  margin: -0.2rem 0 0;
  font-size: 0.58rem;
}

.summary.compact .ribbon-inner {
  padding: 0.4rem 0 0.4rem;
  /* The payout grid and the promotion sit side by side: the ribbon's height is
     the taller of the two instead of their sum, which is the single biggest
     saving on the band (~66px of the phone's 375). */
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  align-items: center;
  gap: 0.3rem 1.6rem;
}

.summary.compact .ribbon-grid {
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.4rem 2rem;
}

.summary.compact .summary-rows {
  gap: 0 1rem;
}

.summary.compact .summary-rows dt {
  font-size: 0.64rem;
  line-height: 1.35;
}

.summary.compact .summary-rows dd {
  font-size: 0.66rem;
  line-height: 1.35;
}

.summary.compact .reward-total {
  font-size: 1.35rem;
}

.summary.compact .reward-total.xp {
  font-size: 0.95rem;
}

.summary.compact .reward-totals {
  justify-items: center;
}

.summary.compact .reward-credits-block {
  gap: 0.15rem;
}

.summary.compact .promotion {
  grid-template-columns: minmax(0, 1fr);
  /* Beside the payout now, so the seam turns vertical with it. */
  padding-top: 0;
  border-top: 0;
  padding-left: 1.2rem;
  border-left: 1px solid rgba(232, 114, 44, 0.22);
}

.summary.compact .ribbon.muted .promotion {
  border-left-color: var(--steel-700);
}

/* A band with one block — the tutorial's pays-nothing note, or a payout whose
   rank block the garage could not answer — takes the whole row rather than
   half of it with a hole beside it. */
.summary.compact .ribbon-inner > :only-child {
  grid-column: 1 / -1;
}

.summary.compact .rank-disc {
  display: none;
}

.summary.compact .promotion-bar {
  height: 6px;
}

.summary.compact .promotion-figures {
  font-size: 0.64rem;
}

.summary.compact .promotion-note {
  margin: 0;
  font-size: 0.54rem;
  line-height: 1.25;
}

.summary.compact .board {
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.summary.compact .board-side {
  padding: 0.3rem 0 0.35rem;
  gap: 0.15rem;
}

.summary.compact .board-head {
  padding: 0 0.6rem 0.15rem;
}

/* The screen scrolls on a phone now, so a short roster keeps a table's height
   rather than collapsing to its rows. */
.summary.compact .board-rows {
  gap: 0.05rem;
  min-height: 7.5rem;
}

.head-xp {
  margin-left: auto;
}

.summary.compact .promotion-head {
  justify-content: flex-start;
}

/* The payout heading is the switch for its breakdown. */
.payout-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.payout-toggle .chevron {
  width: 0.75rem;
  height: 0.75rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 150ms ease;
}

.payout-toggle .chevron.open {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .payout-toggle .chevron {
    transition: none;
  }
}

.summary.compact .board-row {
  padding: 0.04rem 0.3rem;
  font-size: 0.64rem;
}

.summary.compact .board-num {
  font-size: 0.62rem;
}

</style>
